import { Node as PmNode } from '@tiptap/pm/model';

/**
 * Walk a ProseMirror doc and concatenate all text content, mapping each
 * character of the concatenated string back to its absolute PM position.
 * Used by anchor-text resolution to find a comment's anchor span in
 * whatever doc is currently being displayed (master vs. draft).
 *
 * Returns null if the search string isn't found in the doc.
 */
export function findAnchorPositions(
  doc: PmNode,
  search: string,
): { from: number; to: number } | null {
  if (!search) return null;

  // Build [text, position] mapping by walking text nodes.
  const segments: Array<{ pos: number; text: string }> = [];
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      segments.push({ pos, text: node.text });
    }
    return true;
  });

  if (segments.length === 0) return null;

  // Concatenate all text and find the substring.
  const allText = segments.map((s) => s.text).join('');
  const startInText = allText.indexOf(search);
  if (startInText < 0) return null;
  const endInText = startInText + search.length;

  // Map text indices back to PM positions.
  let cursor = 0;
  let from = -1;
  let to = -1;
  for (const seg of segments) {
    const segStart = cursor;
    const segEnd = cursor + seg.text.length;
    if (from < 0 && startInText >= segStart && startInText < segEnd) {
      from = seg.pos + (startInText - segStart);
    }
    if (to < 0 && endInText > segStart && endInText <= segEnd) {
      to = seg.pos + (endInText - segStart);
      break;
    }
    cursor = segEnd;
  }

  if (from < 0 || to < 0 || to <= from) return null;
  return { from, to };
}

/**
 * Verify that the given positions in the doc still hold the expected
 * anchor_text. Used as a fast path before falling back to a search.
 */
export function positionsStillMatch(
  doc: PmNode,
  from: number,
  to: number,
  expected: string,
): boolean {
  if (from < 0 || to <= from) return false;
  if (to > doc.content.size) return false;
  try {
    const actual = doc.textBetween(from, to, ' ', ' ');
    return actual === expected;
  } catch {
    return false;
  }
}
